import {
  CardCarousel,
  CategoryTabs,
  PRODUCT_CARD_SIZE_MAP,
  ProductCardSkeleton,
} from '@iheartjane/menu';
import {
  Box,
  Card,
  Flex,
  ReeferThemeProvider,
  SparkleIcon,
} from '@iheartjane/reefer';
import type { MenuComponentConfig } from '@iheartjane/types';
import { MenuTabEnum } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { getHighlightedSections } from './EditorPreview.util';
import type { SidebarSection } from './EditorSidebar';
import {
  AiButtonSkeleton,
  BUTTON_HEIGHT,
  FilterBarSkeleton,
  FooterSkeleton,
  HeaderSkeleton,
  PreviewSkeleton,
  TEXT_HEIGHT,
} from './sections/EditorPreviewSections';

const DESKTOP_MAX_WIDTH = 1440;
const MOBILE_MAX_WIDTH = 393;
interface EditorPreviewProps {
  /** Active section of the editor sidebar */
  activeSection: SidebarSection | null;
  /** Bloom menu header color */
  headerColor?: string;
  /** Uploaded menu logo */
  logoUrl?: string;
  /** Bloom menu primary/brand color */
  primaryColor?: string;
  /** View mode of the preview, set by the toggle in the modal header */
  viewMode: 'desktop' | 'mobile';
}

export const EditorPreview = ({
  activeSection,
  viewMode,
  headerColor,
  logoUrl,
  primaryColor,
}: EditorPreviewProps) => {
  const { watch } = useFormContext<MenuComponentConfig>();
  const categoryTabsConfig = watch('category_tabs');
  const productCardsConfig = watch('product_cards');
  const searchConfig = watch('search');

  const isMobile = viewMode === 'mobile';
  const whiteHeader =
    headerColor &&
    (headerColor.toLowerCase() === '#ffffff' ||
      headerColor.toLowerCase() === '#fff');
  // Vertical padding accounts for 4px borders for highlighting sections
  // 12 + 4 = 16 on mobile, 20 + 4 = 24 on desktop
  const VERTICAL_PADDING = isMobile ? 12 : 20;
  const HORIZONTAL_PADDING = isMobile ? 16 : 64;

  const highlightedSections = useMemo(
    () => getHighlightedSections(activeSection, searchConfig?.position),
    [activeSection, searchConfig?.position]
  );

  // Sections between the header and filter bar reorder based on settings.
  const sortableSections: Record<string, ReactNode> = {
    category_tabs: (
      <Box
        id="category_tabs"
        data-testid="category_tabs"
        px={HORIZONTAL_PADDING}
        py={VERTICAL_PADDING}
        borderRadius="lg"
        borderWidth="4px"
        border={
          highlightedSections?.includes('category_tabs')
            ? 'purple'
            : 'transparent'
        }
      >
        <CategoryTabs
          categories={Object.values(MenuTabEnum)}
          getUrlString={() => ''}
          preview={true}
          primaryColor={primaryColor || ''}
          selectedCategory="featured"
          variant={categoryTabsConfig?.style}
        />
      </Box>
    ),
    full_width_search:
      searchConfig?.position === 'content_header' ? (
        <Box
          id="full_width_search"
          data-testid="full_width_search"
          px={HORIZONTAL_PADDING}
          py={VERTICAL_PADDING}
          borderRadius="lg"
          borderWidth="4px"
          border={
            highlightedSections?.includes('full_width_search')
              ? 'purple'
              : 'transparent'
          }
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            background="grays-white"
            border="grays-light"
            borderRadius="xl"
            gap={12}
            pl={16}
            pr={8}
            py={8}
            height={isMobile ? BUTTON_HEIGHT : 56}
            width="100%"
          >
            <Flex alignItems="center" gap={12}>
              <PreviewSkeleton height={32} width={32} />
              <PreviewSkeleton height={24} width={100} />
            </Flex>

            {isMobile ? (
              <Flex
                alignItems="center"
                background="primary"
                borderRadius="lg"
                data-testid="ai-button"
                gap={4}
                px={8}
                height={32}
                width={70}
              >
                <SparkleIcon color="grays-white" size="sm" />
                <PreviewSkeleton
                  background="grays-ultralight"
                  height={18}
                  width={34}
                />
              </Flex>
            ) : (
              <AiButtonSkeleton height={40} />
            )}
          </Flex>
        </Box>
      ) : null,
    carousel: (
      <Box
        id="carousel"
        data-testid="carousel"
        px={HORIZONTAL_PADDING}
        py={VERTICAL_PADDING}
      >
        <PreviewSkeleton height={isMobile ? 250 : 320} width="100%" />

        <Flex justifyContent="center" gap={4} mt={isMobile ? 16 : 24}>
          <PreviewSkeleton background="grays-mid" height={6} width={16} />
          {Array.from({ length: 4 }).map((_, i) => (
            <PreviewSkeleton key={i} height={6} width={6} />
          ))}
        </Flex>
      </Box>
    ),
  };

  const sectionsOrder =
    categoryTabsConfig?.position === 'below_carousel'
      ? ['full_width_search', 'carousel', 'category_tabs']
      : ['category_tabs', 'full_width_search', 'carousel'];

  return (
    <ReeferThemeProvider
      theme={{ colors: { primary: primaryColor } }}
      themeId="preview-theme"
    >
      <Flex
        id="preview-theme"
        justifyContent="center"
        // Account for the sidebar width + gutter
        maxWidth="calc(100% - 400px)"
        width="100%"
      >
        <Card
          data-testid="preview-content"
          border="grays-light"
          borderWidth="0"
          height="100%"
          width="100%"
          style={{
            '--card-border-radius': 'var(--border-radius-lg)',
            maxWidth: isMobile ? MOBILE_MAX_WIDTH : DESKTOP_MAX_WIDTH,
          }}
        >
          <Flex
            background="grays-white"
            flexDirection="column"
            overflow="scroll"
          >
            {/* Header */}
            <Flex
              id="header"
              data-testid="header"
              alignItems="center"
              flexDirection="column"
              gap={24}
              width="100%"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
              borderWidth="4px"
              border={
                highlightedSections?.includes('header')
                  ? 'purple'
                  : 'transparent'
              }
              style={{
                backgroundColor: headerColor,
                borderRadius:
                  whiteHeader || highlightedSections?.includes('header')
                    ? 'var(--border-radius-lg)'
                    : 'var(--border-radius-lg) var(--border-radius-lg) 0 0',
              }}
            >
              <HeaderSkeleton
                isMobile={isMobile}
                logoUrl={logoUrl}
                searchPosition={searchConfig?.position}
                whiteHeader={Boolean(whiteHeader)}
              />
            </Flex>

            {sectionsOrder.map((key) => (
              <Fragment key={key}>{sortableSections[key]}</Fragment>
            ))}

            {/* Filter bar */}
            <Flex
              id="filters"
              data-testid="filters"
              justifyContent="space-between"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
              borderRadius="lg"
              borderWidth="4px"
              border={
                highlightedSections?.includes('filters')
                  ? 'purple'
                  : 'transparent'
              }
            >
              <FilterBarSkeleton
                isMobile={isMobile}
                searchPosition={searchConfig?.position}
              />
            </Flex>

            {/* Carousel controls */}
            <Flex
              id="carousel_controls"
              data-testid="carousel_controls"
              alignItems="center"
              justifyContent="space-between"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
            >
              <Flex gap={8} flexDirection="column">
                <PreviewSkeleton
                  height={isMobile ? TEXT_HEIGHT : 32}
                  width={isMobile ? 108 : 156}
                  mb={8}
                />
                <PreviewSkeleton height={TEXT_HEIGHT} width={72} />
              </Flex>

              <Flex gap={16}>
                <PreviewSkeleton height={TEXT_HEIGHT} width={24} />
                <PreviewSkeleton height={TEXT_HEIGHT} width={24} mr={8} />
                <PreviewSkeleton height={TEXT_HEIGHT} width={72} />
              </Flex>
            </Flex>

            {/* Product cards */}
            <Box
              id="product_cards"
              data-testid="product_cards"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
              borderRadius="lg"
              borderWidth="4px"
              border={
                highlightedSections?.includes('product_cards')
                  ? 'purple'
                  : 'transparent'
              }
              width="100%"
            >
              <CardCarousel
                cardWidth={
                  PRODUCT_CARD_SIZE_MAP[
                    productCardsConfig?.card_size || 'medium'
                  ]
                }
                cardWidthMobile={163}
                cardGap={24}
                cardGapMobile={16}
                hideCarouselControls
                mode="ideal-width"
                // Offset margin in card carousel header
                // only appearing because we set no name
                mt={-24}
                name=""
                // Suppress a11y warning for empty heading
                headingAs="p"
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <CardCarousel.Card key={i}>
                    <ProductCardSkeleton {...productCardsConfig} />
                  </CardCarousel.Card>
                ))}
              </CardCarousel>
            </Box>

            {/* Footer */}
            <Box
              id="footer"
              data-testid="footer"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
            >
              <FooterSkeleton isMobile={isMobile} />
            </Box>
          </Flex>
        </Card>
      </Flex>
    </ReeferThemeProvider>
  );
};
